/* eslint-disable react/jsx-no-bind */
import { useCallback, useEffect, useRef, useState } from "react";

import { Command, CommandEmpty, CommandGroup, CommandList } from "~/components/shared/Command";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "~/components/shared/PopoverNext";

import { StepSearchInput } from "~/components/build/Show/routes/list/-components/StepSearch/StepSearchInput";
import { StepSearchItem } from "~/components/build/Show/routes/list/-components/StepSearch/StepSearchItem";
import { useStepSearch } from "~/components/build/Show/routes/list/-components/StepSearch/useStepSearch";
import { useHotkeys } from "react-hotkeys-hook";
import { Key } from "ts-key-enum";
import { debounce } from "lodash";
import { Step } from "~/lib/pipeline";
import { useQuery } from "@tanstack/react-query";
import { stepSearchQueryOptions } from "~/components/build/Show/lib/queries/stepSearch";
import { useBuild } from "~/components/build/Show/lib/BuildContext";

function useSearchResults(search: string) {
  const { build } = useBuild();

  const query = useQuery(stepSearchQueryOptions(build, search));

  return {
    filteredSteps: query.data?.records ?? [],
    totalCount: query.data?.totalCount,
    isPending: query.isFetching,
    isInitialLoading: query.isLoading,
  };
}

export function StepSearch() {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { selectStep } = useStepSearch();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleSelect = useCallback(
    (step: Step) => {
      selectStep(step);
      setOpen(false);
    },
    [selectStep],
  );

  const { filteredSteps, totalCount, isPending, isInitialLoading } = useSearchResults(search);
  const [selectedValue, setSelectedValue] = useState("");

  // Auto-select the first result when results change
  useEffect(() => {
    setSelectedValue(filteredSteps[0]?.uuid ?? "");
  }, [filteredSteps]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useHotkeys("s", () => inputRef.current?.focus(), {
    preventDefault: true,
  });

  const updateSearch = useCallback(
    debounce((value: string) => {
      // If the value is the same as the current search, do nothing
      // This prevents unnecessary state updates and re-renders.
      if (value === search) {
        return;
      }

      setOpen(Boolean(value));
      setSearch(value);
    }, 250),
    [search],
  );

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      updateSearch(event.currentTarget.value);
    },
    [updateSearch],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateSearch(event.currentTarget.value);
    },
    [updateSearch],
  );

  return (
    <Command
      loop={true}
      className="gap-0 p-0"
      shouldFilter={false}
      value={selectedValue}
      onValueChange={setSelectedValue}
    >
      <Popover open={open && !isInitialLoading} onOpenChange={setOpen}>
        <PopoverTrigger disabled={true}>
          <StepSearchInput
            isPending={isPending}
            ref={inputRef}
            onChange={handleChange}
            onKeyUp={handleKeyUp}
            onFocus={() => {
              if (inputRef.current) {
                inputRef.current.select();
                setOpen(Boolean(inputRef.current?.value));
              }
            }}
            onKeyDown={(event) => {
              if (event.key === Key.Escape) {
                if (inputRef.current?.value) {
                  inputRef.current.value = "";
                  updateSearch(""); // Keep state in sync with DOM
                } else {
                  inputRef.current?.blur();
                }
              }
            }}
          />
        </PopoverTrigger>

        <PopoverPortal>
          <PopoverContent
            arrow={false}
            onOpenAutoFocus={(event) => event.preventDefault()}
            data-testid="step-search-results"
            className="mt-2 w-[var(--radix-popper-anchor-width)] p-0 data-[state=closed]:duration-0"
          >
            <CommandList className="max-h-[min(var(--radix-popover-content-available-height),32rem)] scroll-py-2 p-2">
              {filteredSteps.length === 0 && !isInitialLoading && (
                <CommandEmpty>No matching steps</CommandEmpty>
              )}

              <CommandGroup>
                {filteredSteps.map((step) => (
                  <StepSearchItem key={step.uuid} onSelect={handleSelect} step={step} />
                ))}
              </CommandGroup>
            </CommandList>
            {totalCount != null && totalCount > filteredSteps.length && (
              <div className="border-t border-gray-300 px-3 py-2 text-xs text-gray-800">
                Showing {filteredSteps.length} of {totalCount} matches
              </div>
            )}
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </Command>
  );
}
